import { ClickAwayListener } from '@mui/material';
import clsx from 'clsx';
import { LoadingSpinner, Stack } from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import debounce from 'lodash.debounce';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { LastSaved } from 'modules/notes/notePopup/noteView/addendumForm/lastSaved';
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { postUpdateSnapshot } from 'store/clients/clientsActions';
import { selectClientSnapshot, setSnapshotDraft } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import style from './snapshot.module.scss';

const Snapshot = () => {
  const [edit, showEdit] = useState(false);
  const { clientId } = useParams();
  const { organizationId } = getLsUserRoleObject();
  const dispatch = useAppDispatch();
  const [showSection, setShowSection] = useState(true);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const clientSnapshot = useAppSelector((state) => selectClientSnapshot(state.clients, clientId));

  useEffect(() => {
    if (!clientId) return;

    const channel = new BroadcastChannel('note-update');
    channel.onmessage = (event) => {
      const snapshotUpdate = event.data;
      if (
        snapshotUpdate.message === 'SNAPSHOT_UPDATED' &&
        snapshotUpdate.clientId === clientId &&
        typeof snapshotUpdate.snapshot === 'string'
      ) {
        dispatch(setSnapshotDraft({ clientId, snapshot: snapshotUpdate.snapshot }));
      }
    };

    return () => {
      channel.close();
    };
  }, [clientId, dispatch]);

  const openEdit = () => {
    showEdit(true);
    setShowSection(true);
  };

  const handleToggleSection = () => {
    setShowSection(!showSection);
    if (showSection) {
      showEdit(false);
    }
  };

  const autosaveForm = async (values: { snapshot: string }) => {
    if (!clientId) return;
    setIsAutosaving(true);
    const response = await dispatch(
      postUpdateSnapshot({ snapshot: values.snapshot, clientId, organizationId })
    );

    setIsAutosaving(false);
    if (response.meta.requestStatus === 'fulfilled') {
      setLastSaved(new Date().toISOString());
    }
  };

  const debounceFn = useCallback(debounce(autosaveForm, 250), []);

  const handleSnapshotChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!clientId) return;

    const clippedSnapshot = event.target.value.slice(0, 500);
    dispatch(setSnapshotDraft({ clientId, snapshot: clippedSnapshot }));

    // we call the autosave function here to avoid the mess with useEffect
    // so we only trigger autosave when snapshot changes
    // if we were to do this in useEffect, it would trigger autosave on initial render as well
    debounceFn({ snapshot: clippedSnapshot });
  };

  const preventSubmit = (event: FormEvent) => event.preventDefault();

  return (
    <Stack width="100%" data-test-id="profile-snapshot">
      <div className={style.snapshotWrapper}>
        <p className={style.snapshot}>Snapshot</p>
        <div>
          <span
            onClick={openEdit}
            className={clsx('material-icons', style.icon)}
            style={{
              color: edit ? '#8da0b9' : ''
            }}
            data-disabled={edit}
            data-test-id="edit-icon"
          >
            edit
          </span>
          <span
            onClick={handleToggleSection}
            className={clsx('material-icons-outlined', style.icon)}
          >
            {showSection ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
          </span>
        </div>
      </div>
      {!showSection ? null : (
        <>
          {edit && (
            <form onSubmit={preventSubmit}>
              <Stack width="100%" mt="4px">
                <Stack maxHeight="212px">
                  <ClickAwayListener onClickAway={() => showEdit(false)}>
                    <TextAreaInputField
                      onChange={handleSnapshotChange}
                      name="snapshot"
                      value={clientSnapshot}
                      enableCounter
                      maxLength={500}
                      placeholder="Type here"
                      autoFocus
                    />
                  </ClickAwayListener>
                  <Stack display="flex" flexDirection="row" width="100%" height="24px" mt="8px">
                    {isAutosaving ? (
                      <LoadingSpinner size={24} />
                    ) : (
                      <LastSaved lastSaved={lastSaved} />
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </form>
          )}
          {!edit &&
            (clientSnapshot === '' ? (
              <Stack width="100%" mt="4px">
                <Stack maxHeight="212px">
                  <TextAreaInputField
                    onChange={handleSnapshotChange}
                    name="snapshot"
                    value={clientSnapshot}
                    enableCounter
                    maxLength={500}
                    placeholder="Type here"
                    disabled
                  />
                  <Stack
                    display="flex"
                    flexDirection="row"
                    width="100%"
                    height="24px"
                    mt="8px"
                  ></Stack>
                </Stack>
              </Stack>
            ) : (
              <Stack maxHeight="156px" overflow="auto" padding="16px 14px">
                <p className={style.snapshotText}>{clientSnapshot}</p>
              </Stack>
            ))}
        </>
      )}
    </Stack>
  );
};

export default Snapshot;
